/**
 * 주문 검색 및 통계 관련 함수
 * 기존 order.ts에서 분리
 */

import { supabase } from '@/lib/supabase';
import type { Order } from '@/types';

/**
 * 주문 번호로 주문 조회
 */
export async function getOrderByNumber(
  orderNumber: string
): Promise<Order | null> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        items:order_items(
          *,
          product:products!product_id(id, name, original_price),
          offer:product_offers!offer_id(id, offer_type, discount_rate, discounted_price)
        ),
        restaurant:restaurants(id, name, address, phone),
        payment:payments(id, amount, status, transaction_id, paid_at, approved_at)
      `)
      .eq('order_number', orderNumber)
      .single();

    if (error || !data) {
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching order by number:', error);
    return null;
  }
}

/**
 * 주문 번호로 여러 주문 검색
 */
export async function searchOrdersByNumber(
  orderNumbers: string[]
): Promise<Order[]> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        items:order_items(
          *,
          product:products!product_id(id, name, original_price),
          offer:product_offers!offer_id(id, offer_type, discount_rate, discounted_price)
        ),
        restaurant:restaurants(id, name, address, phone),
        payment:payments(id, amount, status, transaction_id, paid_at, approved_at)
      `)
      .in('order_number', orderNumbers);

    if (error || !data) {
      console.error('Error searching orders by numbers:', error);
      return [];
    }

    return data;
  } catch (error) {
    console.error('Error in searchOrdersByNumber:', error);
    return [];
  }
}

/**
 * 주문 통계 조회
 */
export async function getOrderStats(
  restaurantId: string,
  dateRange: { start: Date; end: Date }
): Promise<{
  totalOrders: number;
  totalRevenue: number;
  avgOrderValue: number;
  ordersByType: Record<string, number>;
  ordersByStatus: Record<string, number>;
} | null> {
  try {
    // Get orders within date range
    const { data: orders, error } = await supabase
      .from('orders')
      .select('id, total_amount, order_type, status')
      .eq('restaurant_id', restaurantId)
      .gte('created_at', dateRange.start.toISOString())
      .lte('created_at', dateRange.end.toISOString())
      .neq('status', 'CANCELLED');

    if (error || !orders) {
      console.error('Error fetching order stats:', error);
      return null;
    }

    // Calculate statistics
    const totalOrders = orders.length;
    const totalRevenue = orders.reduce((sum, order) => sum + order.total_amount, 0);
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    // Group by order type
    const ordersByType = orders.reduce((acc, order) => {
      acc[order.order_type] = (acc[order.order_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Group by status
    const ordersByStatus = orders.reduce((acc, order) => {
      acc[order.status] = (acc[order.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalOrders,
      totalRevenue,
      avgOrderValue,
      ordersByType,
      ordersByStatus
    };
  } catch (error) {
    console.error('Error calculating order stats:', error);
    return null;
  }
}
